import {ColumnGroupType} from 'antd/es/table';
import {ColumnsType} from 'antd/lib/table';
import {Key} from 'react';

import {FIXED_COLUMN_THRESHOLD} from '../../../../const';
import {FormattedNumber} from '../../../../provider/locale/formatted-number';
import {Locale} from '../../../../provider/locale/locale';
import {LocationsAnalyticsTableDataType} from '../../../../service/online-presence/online-presence-type';
import {TableSorterHookReturnType} from '../../../../util/antd/table/table-sorter-hook';
import {getEnumValue} from '../../../../util/enum';
import {PROVIDER_TO_ITS_ID_MAP, ProvidersEnum} from '../../../../util/type';

import {LocationsTableColumnsEnum, LocationsTableSorterKeysType} from './locations-table-sorter';

function getChildrenCells(options: {
    catalog: ProvidersEnum;
    getSortOrder: TableSorterHookReturnType<LocationsTableSorterKeysType>['getSortOrder'];
    allowSorting: boolean;
}): ColumnGroupType<LocationsAnalyticsTableDataType>['children'] {
    const {catalog, getSortOrder, allowSorting} = options;

    return [
        {
            key: `${catalog}_${LocationsTableColumnsEnum.CatalogImpressionsSuffix}`,
            dataIndex: `${catalog}_${LocationsTableColumnsEnum.CatalogImpressionsSuffix}`,
            title: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__IMPRESSION_DYNAMICS__IMPRESSIONS" />,
            align: 'center',
            width: 120,
            sorter: allowSorting,
            sortOrder: getSortOrder(`${catalog}_${LocationsTableColumnsEnum.CatalogImpressionsSuffix}`),
            render(_value: unknown, row: LocationsAnalyticsTableDataType): JSX.Element {
                return <FormattedNumber value={row[catalog].impressions} />;
            },
        },
        {
            key: `${catalog}_${LocationsTableColumnsEnum.CatalogConversionSuffix}`,
            dataIndex: `${catalog}_${LocationsTableColumnsEnum.CatalogConversionSuffix}`,
            title: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__IMPRESSION_DYNAMICS__CONVERSION" />,
            align: 'center',
            width: 120,
            sorter: allowSorting,
            sortOrder: getSortOrder(`${catalog}_${LocationsTableColumnsEnum.CatalogConversionSuffix}`),
            render(_value: unknown, row: LocationsAnalyticsTableDataType): JSX.Element {
                return <FormattedNumber value={row[catalog].conversion * 100} />;
            },
        },
        {
            key: `${catalog}_${LocationsTableColumnsEnum.CatalogCallClicksSuffix}`,
            dataIndex: `${catalog}_${LocationsTableColumnsEnum.CatalogCallClicksSuffix}`,
            title: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__TARGET_ACTION_DYNAMICS__CALLS" />,
            align: 'center',
            width: 110,
            sorter: allowSorting,
            sortOrder: getSortOrder(`${catalog}_${LocationsTableColumnsEnum.CatalogCallClicksSuffix}`),
            render(_value: unknown, row: LocationsAnalyticsTableDataType): JSX.Element {
                return <FormattedNumber value={row[catalog].callClicks} />;
            },
        },
        {
            key: `${catalog}_${LocationsTableColumnsEnum.CatalogWebsiteClicksSuffix}`,
            dataIndex: `${catalog}_${LocationsTableColumnsEnum.CatalogWebsiteClicksSuffix}`,
            title: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__TARGET_ACTION_DYNAMICS__SITE" />,
            align: 'center',
            width: 110,
            sorter: allowSorting,
            sortOrder: getSortOrder(`${catalog}_${LocationsTableColumnsEnum.CatalogWebsiteClicksSuffix}`),
            render(_value: unknown, row: LocationsAnalyticsTableDataType): JSX.Element {
                return <FormattedNumber value={row[catalog].websiteClicks} />;
            },
        },
        {
            key: `${catalog}_${LocationsTableColumnsEnum.CatalogDirectionRequestsSuffix}`,
            dataIndex: `${catalog}_${LocationsTableColumnsEnum.CatalogDirectionRequestsSuffix}`,
            title: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__TARGET_ACTION_DYNAMICS__ROUTES" />,
            align: 'center',
            width: 110,
            sortOrder: getSortOrder(`${catalog}_${LocationsTableColumnsEnum.CatalogDirectionRequestsSuffix}`),
            sorter: allowSorting,
            render(_value: unknown, row: LocationsAnalyticsTableDataType): JSX.Element {
                return <FormattedNumber value={row[catalog].directionRequests} />;
            },
        },
        {
            key: `${catalog}_${LocationsTableColumnsEnum.CatalogOtherSuffix}`,
            dataIndex: `${catalog}_${LocationsTableColumnsEnum.CatalogOtherSuffix}`,
            title: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__TARGET_ACTION_DYNAMICS__OTHER" />,
            align: 'center',
            width: 110,
            sortOrder: getSortOrder(`${catalog}_${LocationsTableColumnsEnum.CatalogOtherSuffix}`),
            sorter: allowSorting,
            render(_value: unknown, row: LocationsAnalyticsTableDataType): JSX.Element {
                return <FormattedNumber value={row[catalog].other} />;
            },
        },
        {
            key: `${catalog}_${LocationsTableColumnsEnum.CatalogTotalTargetActionsSuffix}`,
            dataIndex: `${catalog}_${LocationsTableColumnsEnum.CatalogTotalTargetActionsSuffix}`,
            title: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__TARGET_ACTION_DYNAMICS__TOTAL_TARGET_ACTIONS" />,
            align: 'center',
            width: 110,
            sortOrder: getSortOrder(`${catalog}_${LocationsTableColumnsEnum.CatalogTotalTargetActionsSuffix}`),
            sorter: allowSorting,
            render(_value: unknown, row: LocationsAnalyticsTableDataType): JSX.Element {
                return <FormattedNumber value={row[catalog].totalTargetActions} />;
            },
        },
    ];
}

function checkIsProviders(columnKey: Key | ProvidersEnum): columnKey is ProvidersEnum {
    return Boolean(getEnumValue(ProvidersEnum, columnKey));
}

export function getAvailableColumns(
    columns: ColumnsType<LocationsAnalyticsTableDataType>,
    availableCatalogs: Set<ProvidersEnum>,
    regionAvailableCatalogs: Record<string, boolean> | undefined
): ColumnsType<LocationsAnalyticsTableDataType> {
    return columns.filter((column) =>
        column.key && checkIsProviders(column.key)
            ? availableCatalogs.has(column.key) && regionAvailableCatalogs?.[PROVIDER_TO_ITS_ID_MAP[column.key]]
            : true
    );
}

export function getLocationsTableColumns(options: {
    screenWidth: number;
    getSortOrder: TableSorterHookReturnType<LocationsTableSorterKeysType>['getSortOrder'];
    allowSorting: boolean;
}): ColumnsType<LocationsAnalyticsTableDataType> {
    const {screenWidth, getSortOrder, allowSorting} = options;

    return [
        {
            key: 'companyName',
            dataIndex: LocationsTableColumnsEnum.CompanyName,
            title: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__LOCATIONS__TABLE__NAME" />,
            width: 200,
            sortOrder: getSortOrder(LocationsTableColumnsEnum.CompanyName),
            sorter: false,
            fixed: screenWidth > FIXED_COLUMN_THRESHOLD,
        },
        {
            key: 'companyAddress',
            dataIndex: LocationsTableColumnsEnum.CompanyAddress,
            title: <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__LOCATIONS__TABLE__ADDRESS" />,
            width: 200,
            sortOrder: getSortOrder(LocationsTableColumnsEnum.CompanyAddress),
            sorter: false,
            fixed: screenWidth > FIXED_COLUMN_THRESHOLD,
        },
        {
            title: <Locale stringKey="CATEGORY_NAME__ONLINE_PRESENCE__YANDEX" />,
            key: ProvidersEnum.yandex,
            dataIndex: ProvidersEnum.yandex,
            children: getChildrenCells({catalog: ProvidersEnum.yandex, getSortOrder, allowSorting}),
        },
        {
            title: <Locale stringKey="CATEGORY_NAME__ONLINE_PRESENCE__GOOGLE" />,
            key: ProvidersEnum.google,
            dataIndex: ProvidersEnum.google,
            children: getChildrenCells({catalog: ProvidersEnum.google, getSortOrder, allowSorting}),
        },
        {
            title: <Locale stringKey="CATEGORY_NAME__ONLINE_PRESENCE__WGIS" />,
            key: ProvidersEnum.doubleGis,
            dataIndex: ProvidersEnum.doubleGis,
            children: getChildrenCells({catalog: ProvidersEnum.doubleGis, getSortOrder, allowSorting}),
        },
    ];
}

export const locationAnalyticsSorterAllowedCompanyCountThreshold = 5000;
